import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { ErrorT } from '@app/services/rtkQuery/baseQuery';
import {
  useLazyGetIntegration1CByIdQuery,
  useUpdateIntegration1CMutation,
  useUpdateIntegration1COptionsMutation,
} from '@app/services/rtkQuery/SocialIntegrations/api';
import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';

import { normalizeWarehouses } from '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal/utils';

import type {
  EditAuthFormValues,
  EditMode,
  UseEdit1CIntegrationModalProps,
  WarehouseItem,
} from '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal/types';

const { useForm } = Form;

const isAntdFormValidationError = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === 'object' &&
      'errorFields' in (error as Record<string, unknown>)
  );

export const useEdit1CIntegrationModal = ({
  isOpen,
  integrationId,
  initialMode = 'edit',
  onCancel,
  afterSave,
}: UseEdit1CIntegrationModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [form] = useForm<EditAuthFormValues>();

  const [mode, setMode] = useState<EditMode>(initialMode);

  const [currentIntegration, setCurrentIntegration] =
    useState<NSocialIntegrations.Model1cWarehouseIntegration | null>(null);

  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>(
    []
  );
  const [isSaveValidationVisible, setIsSaveValidationVisible] = useState(false);

  const [loadIntegration, { isFetching: isLoadingIntegration }] =
    useLazyGetIntegration1CByIdQuery();

  const [
    updateIntegration,
    { isLoading: isUpdatingIntegration, reset: resetUpdateIntegration },
  ] = useUpdateIntegration1CMutation();

  const [
    updateIntegrationOptions,
    { isLoading: isUpdatingOptions, reset: resetUpdateIntegrationOptions },
  ] = useUpdateIntegration1COptionsMutation();

  const isPrimaryActionLoading =
    isLoadingIntegration || isUpdatingIntegration || isUpdatingOptions;

  const isPrimaryActionDisabled = useMemo(() => {
    if (!isOpen) return true;
    if (!integrationId) return true;
    if (!currentIntegration?.Id && !integrationId) return true;

    if (mode === 'options') {
      return selectedWarehouseIds.length === 0 || isPrimaryActionLoading;
    }

    return isPrimaryActionLoading;
  }, [
    currentIntegration?.Id,
    integrationId,
    isOpen,
    isPrimaryActionLoading,
    mode,
    selectedWarehouseIds.length,
  ]);

  const handleReset = useCallback(() => {
    setMode(initialMode);
    setCurrentIntegration(null);

    setWarehouses([]);
    setSelectedWarehouseIds([]);
    setIsSaveValidationVisible(false);

    resetUpdateIntegration();
    resetUpdateIntegrationOptions();
  }, [initialMode, resetUpdateIntegration, resetUpdateIntegrationOptions]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleModeChange = useCallback((nextMode: EditMode) => {
    setMode(nextMode);
    setIsSaveValidationVisible(false);
  }, []);

  const handleFetchIntegration = useCallback(async () => {
    if (!integrationId) return;

    try {
      const response = await loadIntegration({
        ActiveBranchId,
        integrationId,
      }).unwrap();

      setCurrentIntegration(response);

      form.setFieldsValue({
        serviceUrl: response.Server ?? '',
        login: response.Username ?? '',
        password: response.Password ?? '',
      });

      const normalized = normalizeWarehouses(response.Warehouses);
      setWarehouses(normalized);
      setSelectedWarehouseIds(
        normalized.filter((w) => w.isSelected).map((w) => w.guid1C)
      );
    } catch (error) {
      showNotification(error);
    }
  }, [ActiveBranchId, form, integrationId, loadIntegration, showNotification]);

  const handleToggleWarehouse = useCallback(
    (warehouseGuid: string, checked: boolean) => {
      setSelectedWarehouseIds((prev) => {
        if (checked)
          return prev.includes(warehouseGuid) ? prev : [...prev, warehouseGuid];
        return prev.filter((id) => id !== warehouseGuid);
      });

      setIsSaveValidationVisible(false);
    },
    []
  );

  const handleSaveEdit = useCallback(async () => {
    if (!integrationId && !currentIntegration?.Id) return;

    try {
      const values = await form.validateFields();

      const id = currentIntegration?.Id ?? integrationId;

      await updateIntegration({
        ActiveBranchId,
        Id: id ?? undefined,
        Name: currentIntegration?.Name ?? undefined,
        Server: values.serviceUrl,
        Username: values.login,
        Password: values.password,
      }).unwrap();

      showNotification(
        `Интеграция с 1С ${currentIntegration?.Name ?? ''} успешно обновлена.`,
        'success'
      );
      onCancel();
      afterSave?.();
    } catch (error) {
      if (isAntdFormValidationError(error)) return;

      const err = error as ErrorT;
      const message =
        !!err && 'data' in err ? err.data : 'Не удалось сохранить изменения.';
      showNotification(message);
    }
  }, [
    ActiveBranchId,
    afterSave,
    currentIntegration?.Id,
    currentIntegration?.Name,
    form,
    integrationId,
    onCancel,
    showNotification,
    updateIntegration,
  ]);

  const handleSaveOptions = useCallback(async () => {
    const id = currentIntegration?.Id ?? integrationId;

    if (!id) return;

    if (selectedWarehouseIds.length === 0) {
      setIsSaveValidationVisible(true);
      return;
    }

    try {
      const selectedWarehouses = warehouses
        .filter((warehouse) => selectedWarehouseIds.includes(warehouse.guid1C))
        .map((warehouse) => ({
          Guid1C: warehouse.guid1C,
          WarehouseName: warehouse.name,
          IsSeleted: true,
        }));

      await updateIntegrationOptions({
        ActiveBranchId,
        Id: id,
        Warehouses: selectedWarehouses,
      }).unwrap();

      showNotification('Настройки складов успешно сохранены.');
      onCancel();
      afterSave?.();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    afterSave,
    currentIntegration?.Id,
    integrationId,
    onCancel,
    selectedWarehouseIds,
    showNotification,
    updateIntegrationOptions,
    warehouses,
  ]);

  const handlePrimaryAction = useCallback(() => {
    if (mode === 'edit') {
      void handleSaveEdit();
      return;
    } else {
      void handleSaveOptions();
    }
  }, [handleSaveEdit, handleSaveOptions, mode]);

  const warehousesList = useMemo(
    () =>
      warehouses.map((warehouse) => ({
        id: warehouse.guid1C,
        name: warehouse.name,
      })),
    [warehouses]
  );

  return {
    form,
    mode,
    integrationName: currentIntegration?.Name ?? '1С Склад',
    warehousesList,
    selectedWarehouseIds,
    isLoadingIntegration,
    isPrimaryActionLoading,
    isPrimaryActionDisabled,
    isSaveValidationVisible,
    handleCancel,
    handleReset,
    handleModeChange,
    handleFetchIntegration,
    handleToggleWarehouse,
    handlePrimaryAction,
  };
};
